import { startHoleForGroup, isShotgunRound } from '../golf';
import type { Round } from '../types';

/** Parse a group name like "1A", "10B", "3" into { num, suffix }. */
export function parseGroupNameKey(name: string): { num: number | null; suffix: string } {
  const m = (name ?? '').trim().match(/^(\d+)([A-Za-z]*)$/);
  if (m) return { num: parseInt(m[1], 10), suffix: (m[2] || '').toUpperCase() };
  return { num: null, suffix: '' };
}

/** Return group indexes in display order: sorted by (start_hole, letter_suffix, original_index). */
export function sortedGroupIndexes(round: Round): number[] {
  const teams = round.teams || [];
  const names = round.group_names || [];
  return teams.map((_: unknown, i: number) => i).sort((a: number, b: number) => {
    const holeA = startHoleForGroup(round, a);
    const holeB = startHoleForGroup(round, b);
    if (holeA !== holeB) return holeA - holeB;
    const suffA = parseGroupNameKey(names[a] || '').suffix;
    const suffB = parseGroupNameKey(names[b] || '').suffix;
    if (suffA !== suffB) return suffA.localeCompare(suffB);
    return a - b;
  });
}

/** Map of startHole → count of groups on that hole. */
export function groupsSharingHoleMap(round: Round): Map<number, number> {
  const teams = round.teams || [];
  const counts = new Map<number, number>();
  teams.forEach((_: unknown, i: number) => {
    const h = startHoleForGroup(round, i);
    counts.set(h, (counts.get(h) || 0) + 1);
  });
  return counts;
}

/** Ordinal position (0-based) of groupIndex among all groups sharing its start hole,
 *  ordered by original array index. 0 = first on that hole, 1 = second, etc. */
export function sharedHoleOrdinal(round: Round, groupIndex: number): number {
  const teams = round.teams || [];
  const h = startHoleForGroup(round, groupIndex);
  let ordinal = 0;
  for (let i = 0; i < teams.length; i++) {
    if (i === groupIndex) break;
    if (startHoleForGroup(round, i) === h) ordinal++;
  }
  return ordinal;
}

/** 0 → "", 1 → "A", 2 → "B", … 25 → "Z". */
export function letterForOrdinal(o: number): string {
  if (o <= 0) return '';
  return String.fromCharCode(64 + o);
}

/** Render-time label for a group's start-hole slot, e.g. "H1", "H1A", "H1B". */
export function holeLabelForGroup(round: Round, groupIndex: number): string {
  const h = startHoleForGroup(round, groupIndex);
  return `H${h}${letterForOrdinal(sharedHoleOrdinal(round, groupIndex))}`;
}

// Patterns that indicate a system-generated default (never a user-typed name):
const SYSTEM_DEFAULT_GROUP_RE = /^Group\s+\d+$/;
const SYSTEM_DEFAULT_LETTER_RE = /^\d+[A-Z]$/;

export function isSystemDefaultGroupName(name?: string | null): boolean {
  if (!name) return true;
  const trimmed = String(name).trim();
  if (!trimmed) return true;
  return SYSTEM_DEFAULT_GROUP_RE.test(trimmed) || SYSTEM_DEFAULT_LETTER_RE.test(trimmed);
}

/** Display name for a group: real custom names pass through; system defaults
 *  become "Hole 10" / "Hole 10A" on shotgun rounds, "Group N" elsewhere. */
export function groupDisplayName(round: Round | null | undefined, groupIndex: number): string {
  const stored = round?.group_names?.[groupIndex];
  if (!isSystemDefaultGroupName(stored)) return stored as string;
  if (round && isShotgunRound(round)) {
    const label = holeLabelForGroup(round, groupIndex);
    return `Hole ${label.slice(1)}`;
  }
  return `Group ${groupIndex + 1}`;
}

